import React, { FC, Fragment, useContext, useEffect } from 'react';
import { compose } from 'react-apollo';
import { withTheme } from 'theming';
import { get } from 'lodash';

import tokenStore from 'MoshtixShared/helper-token-store';
import { Icon } from 'MoshtixShared/component-icon';
import LoadingOverlay from 'ControlRoomComponents/loading-overlay';

import Footer from 'Components/component-footer';
import { Header } from 'Components/component-header';
import { ZendeskDialog } from 'Components/component-zendesk-form';

import { useQuery } from '../../hooks/helper-apollo-hooks';
import { GET_ORDERS_N_ASSIGNED_TICKETS_QUERY } from '../../data-definitions';
import { TicketDialog } from '../../components/component-ticket-dialog';
import { TicketDistroContext } from '../../app/contexts';
import { EventDetails } from './component-event-header';
import { TicketList } from './component-ticket-list';
import { withHistory } from './withHistory';
import styles from './styles';

interface EventProps {
  theme: any;
  ticketDialogVisible?: boolean;
  onDialogClose?: () => void;
  loadOrdersIfNeeded?: boolean;
}

export const EventPageNoTheme: FC<EventProps> = (props) => {
  const { ticketDistroContext } = useContext(TicketDistroContext);
  const { state, actions } = ticketDistroContext || {};
  const { dataLoading, orders, assignedTickets } = state || {};

  const token = tokenStore.getTicketDistroToken();

  const dontQuery = !props.loadOrdersIfNeeded || !token || !!orders || !!assignedTickets;

  const { data, loading } = useQuery(GET_ORDERS_N_ASSIGNED_TICKETS_QUERY, {
    skip: dontQuery,
    variables: {
      token: tokenStore.getTicketDistroToken(),
    },
  });

  useEffect(() => {
    actions.setState({
      dataLoading: loading,
    });
  }, [actions, loading]);

  useEffect(() => {
    const viewer = get(data, 'viewer');

    if (viewer) {
      actions.setState({
        assignedTickets: viewer.assignedTickets,
        orders: viewer.orders,
        dataLoading: false,
        dataLoaded: true,
      });
    }
  }, [data, actions]);

  const { theme } = props;

  const infoColor = get(theme, 'palette.info', '#000');

  const isHelpVisible = get(state, 'isHelpVisible', false);
  const helperOrder = get(state, 'helpOrder', 0);

  // use first order for event name currently no cross event ticket functionality exists
  const orderEventName = get(state, 'orders.items[0].event.name', '');
  const assignedTicketEventName = get(state, 'assignedTickets.items[0].event.name', '');
  const eventName = orderEventName || assignedTicketEventName;

  return (
    <Fragment>
      <Header />
      <div>
        <LoadingOverlay visible={dataLoading} />
        <EventDetails {...props} />
        <TicketList {...props} />
        {eventName && (
          <ZendeskDialog
            headerCss={styles.headerCss}
            iconLeft={<Icon type="question" color={infoColor} height={36} width={36} css={styles.iconCss} />}
            headerSubtext={`Order: ${helperOrder}`}
            title={`Help with Order: ${eventName}`}
            show={isHelpVisible}
            preventRedirect
          />
        )}
        {props.ticketDialogVisible && <TicketDialog {...props} />}
        <div className="dialog-root" />
      </div>
      <Footer />
    </Fragment>
  );
};

export const EventPage = compose(withHistory, withTheme)(EventPageNoTheme) as FC<Omit<EventProps, 'theme'>>;

export default EventPage;
